


















































































































































import v8n from 'v8n';
import { debounce } from 'lodash';
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import { OrganisationUser, Organisation } from '@/api';

import authModule from '@/store/Auth';
import snackModule from '@/store/Snack';

import AbstractTable from '@/components/common/AbstractTable.vue';
import { OrganisationUserRole } from '@/api/models/OrganisationUser';

@Component({
  components: {
    AbstractTable,
  },
})
export default class OrganisationUsers extends Vue {
  @Prop({ required: true }) readonly item: Organisation;

  @Prop({ type: Boolean, default: true }) readonly isAdmin: boolean;

  search = '';

  searchQuery = '';

  debounceSearch: () => void;

  userDialog = false;

  isNewUser = false;

  ou: OrganisationUser | null = null;

  canUpdateRole = true;

  sendWelcome = true;

  loading = false;

  valid = false;

  emailRules = [
    (v: string) =>
      v8n()
        .string()
        .not.empty()
        .test(v) || this.$t('users.validation.emailRequired'),

    (v: string) =>
      v8n()
        .pattern(/\S+@\S+\.\S+/)
        .test(v) || this.$t('users.validation.emailValid'),
  ];

  get headers() {
    const listHeaders = OrganisationUser.listHeaders();
    if (this.isAdmin) {
      listHeaders.push({
        text: this.$t('users.labels.geccoUserId'),
        value: 'user.geccoUserId',
      });
    }
    return listHeaders;
  }

  get scopeFactory() {
    return () => {
      return OrganisationUser.includes(['user']).where(this.whereClause);
    };
  }

  get whereClause() {
    if (!this.item) {
      return {};
    }
    return {
      organisation: this.item.id,
      search: this.searchQuery,
    };
  }

  get isOwner() {
    return authModule.isOwner;
  }

  get isUser() {
    return authModule.isUser;
  }

  get isCurrentUser() {
    if (!authModule.user || !this.ou) {
      return false;
    }
    return authModule.user.id === this.ou.user.id;
  }

  get table() {
    return this.$refs.table as AbstractTable;
  }

  get canEditUsers() {
    return this.isAdmin || !this.isUser;
  }

  newUser() {
    if (!authModule.activeOrganisation) {
      throw new Error(
        this.$t('common.alerts.missingActiveOrganisation') as string,
      );
    }
    this.isNewUser = true;
    this.canUpdateRole = true;
    this.userDialog = true;
    this.ou = new OrganisationUser({
      organisation: this.item,
      role: OrganisationUserRole.User,
    });
  }

  editUser(ou: OrganisationUser) {
    if (!authModule.activeOrganisation) {
      throw new Error(
        this.$t('common.alerts.missingActiveOrganisation') as string,
      );
    }
    if (this.isUser && !this.isAdmin) {
      return;
    }
    this.isNewUser = false;
    this.canUpdateRole = this.canEditUsers;
    this.userDialog = true;
    // Use dup() to create a proper clone that preserves persistence state
    this.ou = ou.dup();
  }

  async deleteUser() {
    if (!this.ou) {
      throw new Error(this.$t('users.alerts.missing') as string);
    }
    try {
      await this.ou.destroy();
      snackModule.setSuccess(this.$t('users.alerts.deleted') as string);
      this.userDialog = false;
      this.table.update();
    } catch (e) {
      snackModule.setError({
        text: this.$t('users.alerts.notDeleted') as string,
        errors: e.response.errors,
      });
    }
  }

  async saveUser() {
    if (!authModule.activeOrganisation) {
      throw new Error(
        this.$t('common.alerts.missingActiveOrganisation') as string,
      );
    }
    if (!this.ou) {
      throw new Error(this.$t('users.alerts.missing') as string);
    }
    try {
      this.loading = true;
      let context = {};
      if (this.isNewUser) {
        this.ou.suppressMail = !this.sendWelcome;
        console.log(this.ou);
        context = { with: 'organisation.id' };
      }
      await this.ou.save(context);
      snackModule.setSuccess(this.$t('users.alerts.updated') as string);
      this.userDialog = false;
      this.table.update();
      if (authModule.user && this.ou.user.id === authModule.user.id) {
        // reload page if user is changing themselves
        window.location.reload();
      }
    } catch (e) {
      snackModule.setError({
        text: this.$t('users.alerts.notUpdated') as string,
        errors: e.response.errors,
      });
    } finally {
      this.loading = false;
    }
  }

  mounted() {
    this.debounceSearch = debounce(() => {
      this.searchQuery = this.search;
    }, 500);
  }

  @Watch('whereClause')
  @Watch('order')
  queryChanged() {
    this.table.update();
  }

  @Watch('search')
  searchChanged() {
    this.debounceSearch();
  }
}
